<template lang="pug">
.tiptap-editor-container
  client-only
    div(v-if="editor")
      LoadingOverlay(:loading='uploadingImage')
        editor-content.editor.prose-modern(:editor="editor" v-if="editor")
      div(v-if="props.editable")
        FileUploadWrapper(:after-upload="imageUploaded" ref="fileUploadRef" v-show="false")
        input(type="file" ref="fileInputRef" accept="image/*" style="display: none" @change="handleFileSelect")
        BubbleMenu(
          :editor="editor")
          .flex.flex-row.items-center.justify-center.flex-auto.popbutton-container
            template(v-for="({ slug, option, active, icon }, index) in textActionsFiltered")
              ImageUploadPopover(
                v-if="slug === 'image'"
                :icon="icon"
                :is-active="editor.isActive(active)"
                v-model:href-for-link="hrefForLink"
                :uploading-image="uploadingImage"
                @action-click="onActionClick"
                @use-image-link="useImageLink"
                @upload-file="handleFileUpload"
                @file-input-click="fileInputRef?.click()")
              Popover(v-else-if="slug === 'link'")
                PopoverTrigger(as-child)
                  Button.popbutton(:class="{ 'is-active': editor.isActive(active) }" @native.click="onActionClick(slug, option)")
                    Icon(:name="icon" class="h-5 w-5")
                PopoverContent.p-2.link-popover(side="top" :sideOffset="Number(12)")
                  .flex.flex-row.items-center.justify-center.gap-2
                    Input(
                      v-model="hrefForLink" 
                      placeholder='Enter URL'
                      @keyup.enter="setLink(hrefForLink)")
                    //- Button.action-btn.primary(
                    //-   @click="setLink(hrefForLink)"
                    //-   :disabled="!hrefForLink"
                    //-   title="Use URL")
                    Icon(name="lucide:check" class="h-4 w-4 cursor-pointer" @click="setLink(hrefForLink)" :disabled="!hrefForLink")
              Button.popbutton(v-else :class="{ 'is-active': editor.isActive(active) }" @click="onActionClick(slug, option)")
                Icon(:name="icon" class="h-5 w-5")
        FloatingMenu(
          :editor="editor")
          .flex.flex-row.items-center.justify-center.flex-auto.popbutton-container.border-none.outline-none
            template(v-for="({ slug, option, active, icon, float }, index) in textActions")
              ImageUploadPopover(
                v-if="slug === 'image'"
                :icon="icon"
                :is-active="editor.isActive(active)"
                v-model:href-for-link="hrefForLink"
                :uploading-image="uploadingImage"
                @action-click="onActionClick"
                @use-image-link="useImageLink"
                @upload-file="handleFileUpload"
                @file-input-click="fileInputRef?.click()")
              Button.popbutton(v-else :class="{ 'is-active': editor.isActive(active) }" @click="onActionClick(slug, option)" v-if="float")
                Icon(:name="icon" class="h-5 w-5")
    div(v-if="props.editable && isAdmin")
      //- Separator.divider
      div.flex.justify-between.items-start
        div(class="w-3/4 sm:w-11/12")
          Textarea(v-model="modelValue" v-if="showCode" class="min-h-[100px] resize-y")
        div(class="w-1/4 sm:w-1/12 flex justify-end items-start")
          a.text-base.text-right.w-full.block.show-source(type="info" @click="showCode = !showCode") {{ showCode ? '隐藏' : '编辑' }}源码

    div
      slot#foot
</template>

<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { BubbleMenu, FloatingMenu } from '@tiptap/vue-3/menus'
import { StarterKit } from '@tiptap/starter-kit'
import { TextAlign } from '@tiptap/extension-text-align'
import { Underline } from '@tiptap/extension-underline'
import { Image } from '@tiptap/extension-image'
// import { Subscript } from '@tiptap/extension-subscript'
// import { Superscript } from '@tiptap/extension-superscript'
import { Link } from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extension-placeholder'
import { Typography } from '@tiptap/extension-typography'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'

import LoadingOverlay from '@/components/ui/loading-overlay'
import { toast } from '~/utils/sonner'
import ImageUploadPopover from '@/components/editor/ImageUploadPopover.vue'

interface TiptapProp {
  modelValue?: string
  editable?: boolean
  autofocus?: boolean
}
const props = defineProps<TiptapProp>()

const emit = defineEmits(['update:modelValue'])

const fileUploadRef = ref(null as any)
const fileInputRef = ref<HTMLInputElement | null>(null)

const showCode = ref(false)


const modelValue = computed({
  get() {
    return props.modelValue
  },
  set(val) {
    emit('update:modelValue', val)
  }
})

// const modelValue = defineModel<string>()

const minHeight = computed(() => {
  if (props.editable) {
    return '300px'
  }
  return ''
})

const isAdmin = computed(() => useVisitorSession().isAdmin)

const editable = computed(() => props.editable || false)

const autofocus = computed(() => props.autofocus || false)

const uploadingImage = ref(false)

const i18n = useI18n()

const $t = i18n.t

const generateEditorConfig = () => {
  return {
    content: modelValue.value,
    editable: editable.value,
    autofocus: autofocus.value,
    shouldRerenderOnTransaction: false, // v3 performance optimization
    extensions: [
      StarterKit.configure({
        link: false,      // Disable built-in link to avoid duplicate
        underline: false  // Disable built-in underline to avoid duplicate
      }),
      Typography,
      // FloatingMenu.configure({
      //   element: document.querySelector('.menu'),
      // }),
      Underline,
      // Subscript,
      // Superscript,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image.configure({
        allowBase64: true,
      }),
      Link.configure({
        autolink: false,
        linkOnPaste: true,
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          return $t('placeholder.content')
        },
        showOnlyWhenEditable: true,
      }),
    ],
    editorProps: {
      handlePaste: function (view, event, slice) {
        for (const file of event.clipboardData.files) {
          const ret = imgFileHandler(file)
          if (ret) {
            return true
          }
        }
        return false
      },
      handleDrop: function (view, event, slice) {
        for (const file of event.dataTransfer.files) {
          const ret = imgFileHandler(file)
          if (ret) {
            return true
          }
        }
        return false
      },
    },
    onUpdate: () => {
      if (editor.value) {
        modelValue.value = editor.value.getHTML()
      }
    },
  }
}

const editor = useEditor(generateEditorConfig())

const locale = computed(() => {
  return i18n.locale.value
})

const handleFileUpload = (file) => {
  imgFileHandler(file)
}

const handleFileSelect = (event) => {
  const files = event.target?.files
  if (files && files.length > 0) {
    const file = files[0]
    // Validation is now handled in ImageUploadPopover
    imgFileHandler(file)
  }
  // Reset the input value so the same file can be selected again
  event.target.value = ''
}

const imgFileHandler = (file) => {
  console.log(file)
  // File type validation is now handled in ImageUploadPopover
  if (!file.type.includes('image')) {
    return false
  }
  // no larger than 5MB
  if (file.size >= 5 * 1024 * 1024 && !isAdmin.value) {
    // console.log('file.size <= 512 * 1024', 'using createObjectURL')
    // const url = URL.createObjectURL(file)
    // console.log('createObjectURLurl is:', url)
    // useImageLink(url)
    return toast({
      title: $t('image_upload_fail.title'),
      description: $t('image_upload_fail.file_size_limit', { n: 5 }),
      variant: 'destructive'
    })
  }
  uploadingImage.value = true
  toast({
    title: $t('image_upload_pending.title'),
    duration: 2000,
  })
  fileUploadRef.value?.uploadImage(file).then(useImageLink).then(() => toast({
    title: $t('image_upload_success.title'),
    duration: 2000,
  })).catch((err) => {
    console.log('paste upload err:', err)
    toast({
      title: $t('image_upload_fail.title'),
      description: err.message,
      duration: 2000,
      variant: 'destructive'
    })
  }).finally(() => {
    uploadingImage.value = false
  })
  return true
}

watch(() => props.editable, (val) => {
  console.log('props changed! props.editable: ', props.editable)
  if (!editor?.value || editor.value?.isDestroyed) { return }
  editor.value.setOptions(generateEditorConfig())
})

watch(locale, (val) => {
  if (!editable.value) { return }
  console.log('locale changed', locale.value)
  if (!editor?.value || editor.value?.isDestroyed) { return }
  console.log('modelValue.value is:', modelValue.value, 'length is:', modelValue.value?.length)
  if (modelValue.value?.length && modelValue.value !== '<p></p>') { return }
  console.log('set placeholder', $t('placeholder.content'))
  editor.value.setOptions(generateEditorConfig())
})

function imageUploaded({ error, data, file }) {
  if (!editor?.value || editor.value?.isDestroyed) { return }
  // console.log('image uploaded!', arguments)
  uploadingImage.value = false
  if (error) {
    return toast({
      title: i18n.t('error.upload_img_failed'),
      description: JSON.stringify(error.message || error),
      variant: 'destructive'
    })
  }
  const vm = editor.value.chain().focus()
  vm.setImage({ src: data, title: file.name }).run()
}

const hrefForLink = ref('')

const alignOptions = ['left', 'center', 'right', 'justify']

const textActions = [
  { slug: 'h1', icon: 'bi:type-h1', active: { level: 1 }, float: true },
  { slug: 'h2', icon: 'bi:type-h2', active: { level: 2 }, float: true },
  { slug: 'bold', icon: 'material-symbols:format-bold', active: 'bold', option: '' },
  { slug: 'italic', icon: 'material-symbols:format-italic', active: 'italic' },
  { slug: 'underline', icon: 'material-symbols:format-underlined', active: 'underline' },
  { slug: 'strike', icon: 'material-symbols:format-strikethrough', active: 'strike' },
  // {
  //   slug: 'align',
  //   option: 'left',
  //   icon: MaterialSymbolsFormatAlignLeft,
  //   active: { textAlign: 'left' },
  // },
  {
    slug: 'align',
    option: 'center',
    icon: 'material-symbols:format-align-center',
    active: { textAlign: 'center' },
  },
  // {
  //   slug: 'align',
  //   option: 'right',
  //   icon: MaterialSymbolsFormatAlignRight,
  //   active: { textAlign: 'right' },
  // },
  { slug: 'link', icon: 'material-symbols:add-link', active: 'link' },
  // {
  //   slug: 'align',
  //   option: 'justify',
  //   icon: MaterialSymbolsFormatAlignJustify,
  //   active: { textAlign: 'justify' },
  // },
  // { slug: 'bulletList', icon: MaterialSymbolsFormatListBulleted, active: 'bulletList' },
  // { slug: 'orderedList', icon: MaterialSymbolsFormatListNumbered, active: 'orderedList' },
  // { slug: 'subscript', icon: RiSubscript2, active: 'subscript' },
  // { slug: 'superscript', icon: RiSuperscript2, active: 'superscript', },
  // { slug: 'clear', icon: MaterialSymbolsFormatClear, active: 'clear' },
  { slug: 'code', icon: 'material-symbols:code', active: 'code', float: true },
  { slug: 'quote', icon: 'material-symbols:format-quote', active: 'quote', float: true },
  { slug: 'image', icon: 'material-symbols:add-photo-alternate', active: 'image', float: true },
]

const textActionsFiltered = computed(() => {
  if (!editor.value) { return textActions }
  const isImage = editor.value.isActive('image')
  // const isLink = editor.value.isActive('link')
  return textActions.filter((action) => {
    if (isImage) {
      return action.slug === 'image'
    }
    return true
  })
})

watch(modelValue, (value) => {
  if (!editor?.value || editor.value?.isDestroyed) { return }
  const isSame = editor.value.getHTML() === value
  // JSON
  // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)
  if (isSame) { return }
  editor.value.commands.setContent(value as string)
})

onBeforeUnmount(() => {
  if (editor.value && !editor.value.isDestroyed) {
    editor.value.destroy()
  }
  editor.value = null
})

function onActionClick(slug, option = '') {
  console.log('onActionClick', slug, option)
  if (!editor?.value || editor.value?.isDestroyed) { return }
  const vm = editor.value.chain().focus()
  const actionTriggers = {
    bold: () => vm.toggleBold().run(),
    italic: () => vm.toggleItalic().run(),
    underline: () => vm.toggleUnderline().run(),
    strike: () => vm.toggleStrike().run(),
    bulletList: () => vm.toggleBulletList().run(),
    orderedList: () => vm.toggleOrderedList().run(),
    align: () => {
      // console.log('currentAlign:', editor.value?.getJSON(), editor.value?.getAttributes('heading').textAlign)
      // console.log(editor.value?.getAttributes('paragraph').textAlign)
      const currentAlign = editor.value?.getAttributes('heading').textAlign || editor.value?.getAttributes('paragraph').textAlign
      console.log('currentAlign', currentAlign)
      const currentAlignIndex = alignOptions.indexOf(currentAlign || 'left')
      const nextAlignIndex = (currentAlignIndex + 1) % alignOptions.length
      console.log('nextAlignIndex', alignOptions[nextAlignIndex])
      if (alignOptions[nextAlignIndex]) {
        vm.setTextAlign(alignOptions[nextAlignIndex]).run()
      }
    },
    // subscript: () => vm.toggleSubscript().run(),
    // superscript: () => vm.toggleSuperscript().run(),
    undo: () => vm.undo().run(),
    redo: () => vm.redo().run(),
    clear: () => {
      vm.clearNodes().run()
      vm.unsetAllMarks().run()
    },
    link: () => {
      const currentHref = editor.value?.getAttributes('link').href
      console.log('currentHref', currentHref)
      hrefForLink.value = currentHref || ''
    },
    code: () => vm.toggleCode().run(),
    h1: () => vm.toggleHeading({ level: 1 }).run(),
    h2: () => vm.toggleHeading({ level: 2 }).run(),
    quote: () => vm.toggleBlockquote().run(),
    image: () => {
      const currentImage = editor.value?.getAttributes('image').src
      console.log('currentImage', currentImage)
      hrefForLink.value = currentImage || ''
    },
  }
  actionTriggers[slug]()
}
const setLink = (href) => {
  if (!editor?.value || editor.value?.isDestroyed) { return }
  const vm = editor.value.chain().focus()
  if (!href.length) {
    vm?.unsetLink().run()
  } else {
    vm?.setLink({ href, target: '_blank' }).run()
  }
}

const useImageLink = (href) => {
  console.log('useImageLink', href)
  if (!editor?.value || editor.value?.isDestroyed) { return }
  const vm = editor.value?.chain()?.focus()
  vm?.setImage({ src: href }).run()
}

function onHeadingClick(index) {
  if (!editor.value || editor.value.isDestroyed) { return }
  const vm = editor.value.chain().focus()
  vm.toggleHeading({ level: index }).run()
}
</script>

<style lang="postcss" scoped>
.tiptap-editor-container {
  width: 100%;
  /* min-height: 100px; */
}

.show-source {
  font-size: 0.7rem;
}
</style>

<style lang="postcss">
@reference "@/assets/css/tailwind.css";
.tiptap-editor-container {
  .ProseMirror {
    outline: none;

    p.is-editor-empty:first-child::before {
      color: #adb5bd;
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }

  .divider {
    margin: 0.5rem auto;
  }

  .popbutton-container {
    @apply shadow-2xl;
    border-radius: 9999px;
    overflow: hidden;
  }

  .popbutton {
    @apply py-0 border-none shadow-none outline-none;
    padding-inline: 0.4rem;
    border-radius: 0 !important;
    font-size: 0.78rem;

    &:first-child {
      border-top-left-radius: 9999px !important;
      border-bottom-left-radius: 9999px !important;
      padding-left: 0.7rem;
    }

    &:last-child {
      border-top-right-radius: 9999px !important;
      border-bottom-right-radius: 9999px !important;
      padding-right: 0.7rem;
    }
  }

  /* Modern minimal popover styles */
  .link-popover {
    @apply border-0 bg-white/95 shadow-xl rounded-xl p-0 overflow-hidden;
  }

  .link-popover-content {
    @apply p-0 flex items-center gap-2;


    .link-url-input {
      @apply flex-1 bg-gray-50 border-0 rounded-lg px-3 py-2 text-sm focus:outline-none placeholder:text-gray-400;
    }

    .action-btn.primary {
      @apply w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200;
      @apply bg-blue-600 text-white hover:bg-blue-700 active:scale-95;

      &:disabled {
        @apply opacity-30 cursor-not-allowed hover:bg-blue-600;
      }
    }
  }

  /* Dark mode support */
  .dark {
    .link-popover {
      @apply bg-gray-800/95 text-white;
    }

    .link-popover-content .link-url-input {
      @apply bg-gray-700/50 text-white placeholder:text-gray-500;
    }
  }
}
</style>
